import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { toast } from 'react-hot-toast'
import { useDebounce } from 'utils'
import {
  Button,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Input,
} from 'ui'
import {
  useUserSearch,
  usePromoteUser,
  UserSearchResult,
} from '../../../queries'
import { userUrlForUser } from './helpers'

// Validation schema - only searchQuery, selectedUser is managed separately
const promoteUserSchema = yup.object({
  searchQuery: yup.string().default(''),
})

type PromoteUserFormValues = yup.InferType<typeof promoteUserSchema>

export const PromoteExistingUserForm: React.FC = () => {
  const navigate = useNavigate()

  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(
    null
  )

  const form = useForm<PromoteUserFormValues>({
    resolver: yupResolver(promoteUserSchema),
    defaultValues: {
      searchQuery: '',
    } as PromoteUserFormValues,
  })

  const searchQuery = form.watch('searchQuery')

  const debouncedQuery = useDebounce(searchQuery, 300)

  const { data: searchResults, isLoading: searching } = useUserSearch({
    query: debouncedQuery,
    limit: 10,
  })

  const promoteUser = usePromoteUser()

  const handleSearchChange = (value: string) => {
    form.setValue('searchQuery', value)
    setSelectedUser(null)
  }

  const handleUserSelect = (user: UserSearchResult) => {
    setSelectedUser(user)
    form.setValue('searchQuery', '')
  }

  const handleConfirmPromote = async () => {
    if (!selectedUser) return

    try {
      const toastId = toast.loading('Promoting user to admin...')
      await promoteUser.mutateAsync(selectedUser.id)
      toast.success(
        `Successfully promoted ${selectedUser.emailAddress} to admin`,
        {
          id: toastId,
        }
      )
      // Store the user ID before resetting
      const promotedUserId = selectedUser.id
      setSelectedUser(null)
      form.reset()
      // Navigate to promoted user's detail page
      navigate(userUrlForUser({ id: promotedUserId } as any))
    } catch (error) {
      console.error('Failed to promote user:', error)
      toast.error('Failed to promote user. Please try again.')
    }
  }

  const filteredResults = useMemo(() => {
    if (!searchResults) return []
    // Filter out users who are already admins
    return searchResults.filter((user) => user.accessLevel !== 'ADMIN')
  }, [searchResults])

  const userDisplayName = selectedUser
    ? selectedUser.firstName && selectedUser.lastName
      ? `${selectedUser.firstName} ${selectedUser.lastName}`
      : selectedUser.emailAddress
    : ''

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleConfirmPromote)}>
        <div className="w-full space-y-6">
          <div>
            <p className="text-sm text-gray-600 mb-4">
              Search for an existing user by email address to promote them to
              admin.
            </p>
          </div>

          <FormField
            control={form.control}
            name="searchQuery"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Search by Email</FormLabel>
                <div className="relative">
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Type at least 2 characters to search..."
                      onChange={(e) => {
                        field.onChange(e)
                        handleSearchChange(e.target.value)
                      }}
                    />
                  </FormControl>
                  {searchQuery.length >= 2 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-input rounded-md shadow-md max-h-80 overflow-y-auto">
                      {searching ? (
                        <div className="p-3 text-sm text-gray-500">
                          Searching...
                        </div>
                      ) : filteredResults.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500">
                          No users found. Try a different search term.
                        </div>
                      ) : (
                        filteredResults.map((user) => (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => handleUserSelect(user)}
                            className="w-full text-left px-3 py-2 hover:bg-actionable/10 transition-colors border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-sm">
                              {user.emailAddress}
                            </div>
                            <div className="text-xs text-gray-500">
                              {user.firstName && user.lastName
                                ? `${user.firstName} ${user.lastName}`
                                : 'No name set'}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          {selectedUser && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Confirm</h2>
              <p className="text-sm text-gray-600">
                Promote <strong>{selectedUser.emailAddress}</strong> to an admin
                user?
              </p>
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-md">
                <p className="text-sm text-amber-900">
                  <strong>Warning:</strong> Are you sure you want to promote{' '}
                  <strong>
                    {userDisplayName} ({selectedUser.emailAddress})
                  </strong>{' '}
                  to admin? This will grant them full administrative access to
                  the system.
                </p>
              </div>
              <Button
                type="submit"
                variant="default"
                size="lg"
                disabled={promoteUser.isPending}
              >
                {promoteUser.isPending ? 'Promoting...' : 'Promote to Admin'}
              </Button>
            </div>
          )}
        </div>
      </form>
    </Form>
  )
}
